import { useRef, useCallback } from "react";
import MapGL, { Source, Layer, Popup, MapRef } from "react-map-gl/maplibre";
import "maplibre-gl/dist/maplibre-gl.css";
import type { LayerProps } from "react-map-gl/maplibre";
import { Box, Button, ButtonGroup, Checkbox, FormControlLabel, IconButton, Typography } from "@mui/material";
import { Fullscreen, FullscreenExit, Map as MapIcon, Satellite } from "@mui/icons-material";
import { SamplePopupContent, CorePopupContent } from "../shared/MissionPopups";
import { OPS_COLORS, type OpsMapProps } from "./OpsMap.types";
import { useOpsMapViewModel } from "./OpsMap.vm";
import type { Job } from "../../../models";
import type { Robot } from "../../../models/rogo/Robot";

// ── Layer specs ─────────────────────────────────────────────────────────────

// Job layers — reuse color from feature properties (set by processStandardJobs)
const jobPolygonFill: LayerProps = {
	id: "ops-job-polygon-fill",
	type: "fill",
	paint: {
		"fill-color": ["get", "color"],
		"fill-opacity": ["coalesce", ["get", "fillOpacity"], ["get", "opacity"], 0.4],
	},
};

const jobPolygonLine: LayerProps = {
	id: "ops-job-polygon-line",
	type: "line",
	paint: {
		"line-color": ["coalesce", ["get", "strokeColor"], ["get", "color"]],
		"line-width": ["coalesce", ["get", "strokeWeight"], 2],
		"line-opacity": ["coalesce", ["get", "strokeOpacity"], 1],
	},
};

const jobPointCircle: LayerProps = {
	id: "ops-job-point-circles",
	type: "circle",
	paint: {
		"circle-radius": 7,
		"circle-color": ["get", "color"],
		"circle-stroke-width": 1.5,
		"circle-stroke-color": "#666",
	},
};

const jobPointLabel: LayerProps = {
	id: "ops-job-point-labels",
	type: "symbol",
	layout: {
		"text-field": ["get", "label"],
		"text-size": 10,
		"text-offset": [0, -1.2],
		"text-anchor": "bottom",
		"text-allow-overlap": false,
	},
	paint: {
		"text-color": "#fff",
		"text-halo-color": "#000",
		"text-halo-width": 1,
	},
};

// Mission layers
const missionPolygonFill: LayerProps = {
	id: "ops-mission-polygon-fill",
	type: "fill",
	paint: {
		"fill-color": OPS_COLORS.missionPolygonFill,
		"fill-opacity": 0.25,
	},
};

const missionPolygonLine: LayerProps = {
	id: "ops-mission-polygon-line",
	type: "line",
	paint: {
		"line-color": OPS_COLORS.missionPolygonStroke,
		"line-width": 2,
	},
};

const missionLineString: LayerProps = {
	id: "ops-mission-lines",
	type: "line",
	paint: {
		"line-color": OPS_COLORS.missionLine,
		"line-width": 2,
	},
};

const missionPointCircle: LayerProps = {
	id: "ops-mission-points",
	type: "circle",
	paint: {
		"circle-radius": 5,
		"circle-color": [
			"case",
			[
				"all",
				["has", "core_location"],
				[
					"any",
					["!", ["has", "pulled_lat"]],
					["!", ["has", "pulled_lon"]],
					["==", ["to-number", ["get", "pulled_lat"], 0], 0],
					["==", ["to-number", ["get", "pulled_lon"], 0], 0],
				],
			],
			"#ef5350",
			"#ffffff",
		],
		"circle-stroke-width": 1.5,
		"circle-stroke-color": "#666",
	},
};

const missionPointLabel: LayerProps = {
	id: "ops-mission-point-labels",
	type: "symbol",
	layout: {
		"text-field": ["get", "rogo_id"],
		"text-size": 11,
		"text-offset": [0, -1.2],
		"text-anchor": "bottom",
		"text-allow-overlap": false,
	},
	paint: {
		"text-color": "#333",
		"text-halo-color": "#fff",
		"text-halo-width": 1.5,
	},
};

// Robot layers
const robotTrailLine: LayerProps = {
	id: "ops-robot-trail",
	type: "line",
	paint: {
		"line-color": OPS_COLORS.robotTrail,
		"line-width": 2,
		"line-opacity": 0.7,
	},
};

const robotPointCircle: LayerProps = {
	id: "ops-robot-points",
	type: "circle",
	paint: {
		"circle-radius": 8,
		"circle-color": OPS_COLORS.robotPoint,
		"circle-stroke-width": 2,
		"circle-stroke-color": "#fff",
	},
};

const robotPointLabel: LayerProps = {
	id: "ops-robot-labels",
	type: "symbol",
	layout: {
		"text-field": ["get", "name"],
		"text-size": 12,
		"text-offset": [0, -1.5],
		"text-anchor": "bottom",
		"text-allow-overlap": false,
	},
	paint: {
		"text-color": "#333",
		"text-halo-color": "#fff",
		"text-halo-width": 1.5,
	},
};

// ── Popup content ───────────────────────────────────────────────────────────

function JobPopupContent({ job }: { job: Job }) {
	return (
		<Box sx={{ minWidth: 140 }}>
			<Typography variant="subtitle2" sx={{ mb: 0.5 }}>
				{job.fieldName || "Unknown Field"}
			</Typography>
			{job.farmName && (
				<Typography variant="body2" sx={{ fontSize: "0.75rem" }}>
					<strong>Farm:</strong> {job.farmName}
				</Typography>
			)}
			{job.growerName && (
				<Typography variant="body2" sx={{ fontSize: "0.75rem" }}>
					<strong>Grower:</strong> {job.growerName}
				</Typography>
			)}
		</Box>
	);
}

function RobotPopupContent({ robot }: { robot: Robot }) {
	return (
		<Box sx={{ minWidth: 140 }}>
			<Typography variant="subtitle2" sx={{ mb: 0.5 }}>
				{robot.name}
			</Typography>
			<Typography variant="body2" sx={{ fontSize: "0.75rem" }}>
				<strong>Lat:</strong> {robot.latitude.toFixed(6)}
			</Typography>
			<Typography variant="body2" sx={{ fontSize: "0.75rem" }}>
				<strong>Lng:</strong> {robot.longitude.toFixed(6)}
			</Typography>
		</Box>
	);
}

// ── Legend ───────────────────────────────────────────────────────────────────

function LayerLegend({
	visibleLayers,
	toggleLayer,
	hasJobs,
	hasMissions,
	hasRobots,
}: {
	visibleLayers: { jobs: boolean; missions: boolean; robots: boolean };
	toggleLayer: (layer: "jobs" | "missions" | "robots") => void;
	hasJobs: boolean;
	hasMissions: boolean;
	hasRobots: boolean;
}) {
	const items = [
		{ key: "jobs" as const, label: "Submitted", color: "#2196f3", has: hasJobs },
		{ key: "missions" as const, label: "Mission", color: OPS_COLORS.missionPolygonFill, has: hasMissions },
		{ key: "robots" as const, label: "Robot", color: OPS_COLORS.robotTrail, has: hasRobots },
	];

	const visibleItems = items.filter((i) => i.has);
	if (visibleItems.length <= 1) return null;

	return (
		<Box
			sx={{
				position: "absolute",
				top: 8,
				left: 8,
				zIndex: 1,
				bgcolor: "background.paper",
				boxShadow: 1,
				borderRadius: 1,
				px: 1,
				py: 0.5,
			}}
		>
			{visibleItems.map((item) => (
				<FormControlLabel
					key={item.key}
					sx={{ display: "flex", mr: 0, "& .MuiFormControlLabel-label": { fontSize: "0.75rem" } }}
					control={
						<Checkbox
							size="small"
							checked={visibleLayers[item.key]}
							onChange={() => toggleLayer(item.key)}
							sx={{
								p: 0.5,
								color: item.color,
								"&.Mui-checked": { color: item.color },
							}}
						/>
					}
					label={item.label}
				/>
			))}
		</Box>
	);
}

// ── Main component ──────────────────────────────────────────────────────────

export function OpsMap(props: OpsMapProps) {
	const vm = useOpsMapViewModel(props);
	const mapRef = useRef<MapRef>(null);

	const height = props.height ?? 400;
	const resolvedHeight = typeof height === "number" ? `${height}px` : height;

	const handleLoad = useCallback(() => {
		vm.fitBounds(mapRef);
	}, [vm.fitBounds]);

	const handleMouseEnter = useCallback(() => {
		const map = mapRef.current;
		if (map) map.getCanvas().style.cursor = "pointer";
	}, []);

	const handleMouseLeave = useCallback(() => {
		const map = mapRef.current;
		if (map) map.getCanvas().style.cursor = "";
	}, []);

	const hasJobs = vm.jobData.polygonFC.features.length > 0 || vm.jobData.pointFC.features.length > 0;
	const hasMissions =
		vm.missionData.polygonFC.features.length > 0 ||
		vm.missionData.allPointFC.features.length > 0 ||
		vm.missionData.lineFC.features.length > 0;
	const hasRobots = vm.robotData.pointFC.features.length > 0 || vm.robotData.trailFC.features.length > 0;

	if (!vm.hasAnyData) {
		return (
			<Box
				sx={{
					height: resolvedHeight,
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					border: 1,
					borderColor: "divider",
					borderRadius: 1,
				}}
			>
				<Typography variant="body2" color="text.secondary">
					No map data available
				</Typography>
			</Box>
		);
	}

	return (
		<Box
			sx={{
				height: vm.fullscreen ? "100vh" : resolvedHeight,
				borderRadius: vm.fullscreen ? 0 : 1,
				overflow: "hidden",
				position: vm.fullscreen ? "fixed" : "relative",
				...(vm.fullscreen && { top: 0, left: 0, right: 0, bottom: 0, zIndex: 1300 }),
			}}
		>
			{/* Layer legend */}
			<LayerLegend
				visibleLayers={vm.visibleLayers}
				toggleLayer={vm.toggleLayer}
				hasJobs={hasJobs}
				hasMissions={hasMissions}
				hasRobots={hasRobots}
			/>

			{/* Mission point view toggle */}
			{vm.visibleLayers.missions && vm.showMissionPointToggle && (
				<ButtonGroup
					size="small"
					sx={{
						position: "absolute",
						top: 8,
						left: "50%",
						transform: "translateX(-50%)",
						zIndex: 1,
						bgcolor: "background.paper",
						boxShadow: 1,
					}}
				>
					<Button
						variant={vm.missionPointView === "samples" ? "contained" : "outlined"}
						onClick={() => vm.setMissionPointView("samples")}
					>
						Samples
					</Button>
					<Button
						variant={vm.missionPointView === "cores" ? "contained" : "outlined"}
						onClick={() => vm.setMissionPointView("cores")}
					>
						Cores
					</Button>
					<Button
						variant={vm.missionPointView === "all" ? "contained" : "outlined"}
						onClick={() => vm.setMissionPointView("all")}
					>
						All
					</Button>
				</ButtonGroup>
			)}

			{/* Top-right controls */}
			<Box sx={{ position: "absolute", top: 8, right: 8, zIndex: 1, display: "flex", gap: 0.5 }}>
				<IconButton
					size="small"
					onClick={vm.toggleTileSource}
					title={vm.tileSource.label}
					sx={{
						bgcolor: "background.paper",
						boxShadow: 1,
						"&:hover": { bgcolor: "action.hover" },
					}}
				>
					{vm.tileSource.id === "google-satellite" ? <MapIcon /> : <Satellite />}
				</IconButton>
				<IconButton
					size="small"
					onClick={() => {
						vm.setFullscreen((prev) => !prev);
						setTimeout(() => {
							mapRef.current?.resize();
							vm.fitBounds(mapRef);
						}, 50);
					}}
					sx={{
						bgcolor: "background.paper",
						boxShadow: 1,
						"&:hover": { bgcolor: "action.hover" },
					}}
				>
					{vm.fullscreen ? <FullscreenExit /> : <Fullscreen />}
				</IconButton>
			</Box>

			<MapGL
				ref={mapRef}
				initialViewState={{ latitude: 40, longitude: -95, zoom: 4 }}
				style={{ width: "100%", height: "100%" }}
				mapStyle={vm.tileSource.style}
				onLoad={handleLoad}
				onClick={vm.handleClick}
				onMouseEnter={handleMouseEnter}
				onMouseLeave={handleMouseLeave}
				interactiveLayerIds={vm.interactiveLayerIds}
			>
				{/* === Job layers (bottom) === */}
				{vm.visibleLayers.jobs && vm.jobData.polygonFC.features.length > 0 && (
					<Source id="ops-job-polygons" type="geojson" data={vm.jobData.polygonFC}>
						<Layer {...jobPolygonFill} />
						<Layer {...jobPolygonLine} />
					</Source>
				)}

				{/* === Mission layers (middle) === */}
				{vm.visibleLayers.missions && vm.missionData.polygonFC.features.length > 0 && (
					<Source id="ops-mission-polygons" type="geojson" data={vm.missionData.polygonFC}>
						<Layer {...missionPolygonFill} />
						<Layer {...missionPolygonLine} />
					</Source>
				)}
				{vm.visibleLayers.missions && vm.missionData.lineFC.features.length > 0 && (
					<Source id="ops-mission-lines" type="geojson" data={vm.missionData.lineFC}>
						<Layer {...missionLineString} />
					</Source>
				)}

				{/* === Robot trail (above mission lines) === */}
				{vm.visibleLayers.robots && vm.robotData.trailFC.features.length > 0 && (
					<Source id="ops-robot-trail" type="geojson" data={vm.robotData.trailFC}>
						<Layer {...robotTrailLine} />
					</Source>
				)}

				{/* === Points on top === */}
				{vm.visibleLayers.jobs && vm.jobData.pointFC.features.length > 0 && (
					<Source id="ops-job-points" type="geojson" data={vm.jobData.pointFC}>
						<Layer {...jobPointCircle} />
						<Layer {...jobPointLabel} />
					</Source>
				)}
				{vm.visibleLayers.missions && vm.missionPointFC.features.length > 0 && (
					<Source id="ops-mission-points" type="geojson" data={vm.missionPointFC}>
						<Layer {...missionPointCircle} />
						<Layer {...missionPointLabel} />
					</Source>
				)}
				{vm.visibleLayers.robots && vm.robotData.pointFC.features.length > 0 && (
					<Source id="ops-robot-points" type="geojson" data={vm.robotData.pointFC}>
						<Layer {...robotPointCircle} />
						<Layer {...robotPointLabel} />
					</Source>
				)}

				{/* === Popup === */}
				{vm.popup && (
					<Popup
						longitude={vm.popup.lng}
						latitude={vm.popup.lat}
						onClose={() => vm.setPopup(null)}
						closeOnClick={false}
						anchor="bottom"
						maxWidth="300px"
					>
						{vm.popup.type === "job" && <JobPopupContent job={vm.popup.job} />}
						{vm.popup.type === "sample" && <SamplePopupContent sample={vm.popup.sample} />}
						{vm.popup.type === "core" && <CorePopupContent core={vm.popup.core} />}
						{vm.popup.type === "robot" && <RobotPopupContent robot={vm.popup.robot} />}
					</Popup>
				)}
			</MapGL>
		</Box>
	);
}
